// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// types
import {
	CsvField,
	CsvRow,
	Entity,
	EntityLoanPayload,
	EntityPayload,
	EntitySubtype,
	ErrorCause,
	ExcelSheet,
	Feedback,
	FormField,
	Loan,
	MetadataField,
	UserPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useEntityForms } from '../../../hooks/useEntityForms';

// mui
import { Alert, AlertTitle, Box, Checkbox, FormControlLabel, Grid } from '@mui/material';

// icons
import {
	PeopleOutlineOutlined as IconPeople,
	PersonOutlined as IconPerson,
} from '@mui/icons-material';

// forms
import { FormTableFields } from '../FormTableFields/FormTableFields';

// fields
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';
import { InputFile } from '../../fields/InputFile/InputFile';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';
import { BannerProps } from '../../ui/Banner/Banner';

// utils
import { isCsvFile, isExcelFile, parseCsv, parseExcel, readCsvFile } from '../../../utils/files';
import { permissionsBeneficiary } from '../../../utils/permissions';
import { translateError } from '../../../utils/translations';

// props
export interface FormUploadBeneficiariesProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormUploadBeneficiaries = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormUploadBeneficiariesProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const { currencies, getDefaultFields } = useEntityForms();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// vars
	const formId = 'form-upload-beneficiaries';
	const isWfa = user?.role === 'WFA';
	const userEntityId = userEntity?.id;

	// state
	const [availableSheets, setAvailableSheets] = useState<ExcelSheet[]>([]);
	const [beneficiaryType, setBeneficiaryType] = useState<EntitySubtype>('ORGANIZATION');
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [entity, setEntity] = useState('');
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [warnings, setWarnings] = useState<string[]>([]);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedSheet, setSelectedSheet] = useState('');

	// fields state
	const fieldsDefault = getDefaultFields({ form: `BENEFICIARY_${beneficiaryType}` });
	const [fields, setFields] = useState<CsvField[]>(fieldsDefault);

	// fetch entities
	useFetch({
		isEnabled: Boolean(!userEntity),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=INTERMEDIARY,PIU,SUB_PIU&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch entity loans
	let urlLoans = `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,status`;

	if (isWfa && entity) {
		urlLoans += `&entity=${entity}`;
	}

	const { fetchRequest: fetchLoans } = useFetch({
		isLazy: true,
		url: urlLoans,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// options
	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: String(entity.id),
		};
	});

	const optionsLoans = loans.map((loan) => {
		return {
			disabled: loan.status === 'DISABLED',
			label: loan.code,
			value: loan.id,
		};
	});

	const optionsHeaders = csvHeaders.map((header: string) => {
		return {
			label: header,
			value: header,
		};
	});

	const optionsSheets = availableSheets.map((sheet) => {
		return {
			label: `${sheet.name} (${sheet.rowCount - 1} ${t('rows').toLowerCase()})`,
			value: sheet.name,
		};
	});

	// inputs
	const radioInputs = [
		{
			icon: <IconPerson />,
			label: t('individual'),
			value: 'INDIVIDUAL',
		},
		{
			icon: <IconPeople />,
			label: t('organization'),
			value: 'ORGANIZATION',
		},
	];

	// fetch form
	const { fetchRequest: fetchForm } = useFetch({
		isLazy: true,
		isEnabled: Boolean(project?.id && currencies.length),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${userEntityId || entity}&project=${
			project?.id
		}&name=${`BENEFICIARY_${beneficiaryType}`}&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]) {
				// add metadata prefix to dynamic fields
				const fieldsMetadata: CsvField[] = res.data[0].fields.map((field: FormField) => {
					return {
						...field,
						examples: [],
						header: null,
						name: `metadata-${field.name}`,
					};
				});

				setFields([...fieldsDefault, ...fieldsMetadata]);
			}
		},
	});

	// handlers
	const handleChangeBeneficiaryType = (
		e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
		value: string
	) => {
		setBeneficiaryType(value as EntitySubtype);
	};

	const handleSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length === 1) {
			const file = e.target.files[0];
			setSelectedFile(file);

			// Clear previous data
			setCsvHeaders([]);
			setCsvRows([]);
			setAvailableSheets([]);
			setSelectedSheet('');
			setFeedback(null);
			setWarnings([]);

			try {
				if (isCsvFile(file.name)) {
					// Handle CSV file
					const text = await readCsvFile(file);
					handleCsv(text);
				} else if (isExcelFile(file.name)) {
					// Handle Excel file
					const result = await parseExcel({ file });
					setAvailableSheets(result.sheets);

					// If only one sheet, auto-select it and load data
					if (result.sheets.length === 1) {
						setSelectedSheet(result.sheets[0].name);
						setCsvHeaders(result.headers);
						setCsvRows(result.rows);
					}
				} else {
					throw new Error('Unsupported file format', {
						cause: {
							id: 'FILE_INVALID_FORMAT',
						},
					});
				}
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				setFeedback({
					alert: {
						children: translateError({ cause, t }),
						severity: 'error',
					},
				});
			}
		}
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// clear feedback
		setFeedback(null);

		// set loading
		setIsLoading(true);

		try {
			// define form data
			const form = document.querySelector(`#${formId}`) as HTMLFormElement;
			const fd = new FormData(form);
			const entries = Object.fromEntries(fd.entries());

			// define beneficiaries
			const beneficiaries = [];

			// define loans
			const beneficiaryLoans: EntityLoanPayload[] = [];
			const entryLoans = (entries.loans as string).split(',');

			// for each loan entry
			for (let i = 0; i < entryLoans.length; i++) {
				// add loan
				beneficiaryLoans.push({
					id: Number(entryLoans[i]),
					parent: Number(userEntityId || entity),
				});
			}

			// for each row
			for (let i = 0; i < csvRows.length; i++) {
				const row = csvRows[i];
				const beneficiary: Partial<EntityPayload> = {
					isKycVerified: true,
					loans: beneficiaryLoans,
					metadata: [],
					project: project?.id,
					subtype: beneficiaryType,
					type: 'BENEFICIARY',
					users: [],
				};

				const beneficiaryUser: UserPayload = {
					country: project?.country?.id || null,
					email: null,
					firstName: '',
					lastName: '',
					projects: [
						{
							id: project?.id,
							permissions: { ...permissionsBeneficiary },
						},
					],
					phone: null,
					role: 'GENERAL',
				};

				// for each field
				for (let f = 0; f < fields.length; f++) {
					const field = fields[f];

					if (!field.header && field.isRequired) {
						throw new Error('Select corresponding fields for all the required fields.', {
							cause: {
								id: 'BULK_UPLOAD_FIELD_EMPTY',
							},
						});
					}

					if (field.header) {
						let value = row[field.header];

						if (value === '') {
							value = null;
						}

						let key: string;

						// field type validations
						if (value) {
							// validate SELECT
							if (field?.type === 'SELECT') {
								const validOptions = field?.options?.map((option) => option.value);
								if (validOptions && !validOptions.includes(value as string)) {
									throw new Error(
										`The value for the ${
											field.label
										} field must be one of the following: ${validOptions.join(', ')}`,
										{
											cause: {
												id: 'FORM_FIELD_SELECT_INVALID',
												details: {
													label: field.label,
													options: validOptions.join(', '),
												},
											},
										}
									);
								}
							}

							// validate NUMBER
							if (field?.type === 'NUMBER') {
								// remove non numerical values
								value = String(value).replace(/[^\d.]/g, '');

								if (value === '') {
									throw new Error(`The value for the ${field.label} field must be a number`, {
										cause: {
											id: 'FORM_FIELD_NUMBER_INVALID',
											details: {
												label: field.label,
											},
										},
									});
								}

								// define value as number
								value = Number(value);
							}

							// validate CHECKBOX
							if (field?.type === 'CHECKBOX') {
								const trueCheckboxValues = [1, 'true', 'TRUE'];
								const falseCheckboxValues = [0, 'false', 'FALSE'];
								const acceptedCheckboxValues = trueCheckboxValues.concat(falseCheckboxValues);
								const validCheckboxValues = acceptedCheckboxValues.join(', ');

								if (!acceptedCheckboxValues.includes(value as string | number)) {
									throw new Error(
										`The value for ${field.label} must be one of the following: ${validCheckboxValues}`,
										{
											cause: {
												id: 'FORM_FIELD_CHECKBOX_INVALID',
												details: {
													label: field.label,
													options: validCheckboxValues,
												},
											},
										}
									);
								}

								// define value as boolean
								value = Boolean(trueCheckboxValues.includes(value as string));
							}

							// validate DATE
							if (field?.type === 'DATE') {
								// define value as date
								value = new Date(value as Date | string);

								if (value.toString() === 'Invalid Date') {
									throw new Error(`The value for ${field.label} must be a date`, {
										cause: {
											id: 'FORM_FIELD_DATE_INVALID',
											details: {
												label: field.label,
											},
										},
									});
								}
							}

							// validate country
							if (
								field.name === 'metadata-country' &&
								field.isRequired &&
								String(value).length !== 2
							) {
								throw new Error(
									'Countries must be formatted using two-letter ISO 3166-1 alpha-2 codes.',
									{
										cause: {
											id: 'COUNTRY_CODE_INVALID',
										},
									}
								);
							}
						}

						if (field.name.includes('metadata-')) {
							// group metadata
							key = field.name.replace('metadata-', '');
							(beneficiary.metadata as MetadataField[]).push({
								isProtected: Boolean(field.isProtected),
								label: field.label,
								name: key,
								type: field.type,
								value,
							});
						} else if (field.name.includes('user-')) {
							// group user
							key = field.name.replace('user-', '');
							beneficiaryUser[key as keyof UserPayload] = value;
						} else {
							// set beneficiary key and value
							key = field.name;
							beneficiary[key] = value;
						}
					}
				}

				// ensure email or phone is set
				if (!beneficiaryUser.email && !beneficiaryUser.phone) {
					throw new Error('User phone or email is empty.', {
						cause: {
							id: 'USER_PHONE_OR_EMAIL_EMPTY',
						},
					});
				}

				// add beneficiary user
				if (beneficiary.users) {
					beneficiary.users.push(beneficiaryUser);
				}

				// define name for individuals
				if (beneficiaryType === 'INDIVIDUAL') {
					beneficiary.name = `${beneficiaryUser.firstName} ${beneficiaryUser.lastName}`;
				}

				// add beneficiary
				beneficiaries.push(beneficiary);
			}

			const fetchPostBeneficiaries = await fetch(`${process.env.REACT_APP_API_URL}/entities`, {
				method: 'post',
				body: JSON.stringify(beneficiaries),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resPostBeneficiaries = await fetchPostBeneficiaries.json();

			if (resPostBeneficiaries.error) {
				throw new Error(resPostBeneficiaries.error.message, {
					cause: {
						id: resPostBeneficiaries.error.id,
						res: resPostBeneficiaries,
					},
				});
			}

			const failedLength = resPostBeneficiaries.data.failed.length;
			const succeededLength = resPostBeneficiaries.data.succeeded.length;

			const banner: BannerProps = {
				hasClose: true,
				message: t('alert.beneficiariesUploadedPartial', {
					failed: failedLength,
					succeeded: succeededLength,
				}),
				severity: 'info',
			};

			const allSucceed = failedLength === 0;
			const allFailed = failedLength === beneficiaries.length;

			if (onSuccess) {
				onSuccess();
			}

			if (resPostBeneficiaries.data.failed.length) {
				let msg = t('alert.beneficiariesUploadedError');
				if (allFailed) {
					msg = t('alert.beneficiariesUploadedError');
				} else if (succeededLength === 1 && failedLength === 1) {
					msg = t('alert.beneficiariesUploadedPartialOneSucceededOneFailed');
				} else if (succeededLength === 1) {
					msg = t('alert.beneficiariesUploadedPartialOneSucceeded', {
						failed: failedLength,
					});
				} else if (failedLength === 1) {
					msg = t('alert.beneficiariesUploadedPartialOneFailed', {
						succeeded: succeededLength,
					});
				} else {
					msg = t('alert.beneficiariesUploadedPartial', {
						failed: failedLength,
						succeeded: succeededLength,
					});
				}
				throw new Error(msg, {
					cause: {
						id: 'BULK_UPLOAD_INCOMPLETE',
						res: resPostBeneficiaries,
					},
				});
			}

			if (allSucceed) {
				banner.message = t('alert.beneficiariesUploadedSuccess');
				banner.severity = 'success';
			}

			// set banner
			setBanner(banner);

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			const errors = [];

			if (cause.id === 'BULK_UPLOAD_INCOMPLETE') {
				for (const err of cause.res.data.failed) {
					if (err.error.id === 'ENTITY_DUPLICATE') {
						errors.push(t('error.entityDuplicate', { id: err.payload.idExternal }));
					}

					if (err.error.id === 'USER_DUPLICATE') {
						if (err.error.details?.email) {
							errors.push(t('error.userDuplicateEmail', { email: err.error.details.email }));
						} else if (err.error.details?.phone) {
							errors.push(t('error.userDuplicatePhone', { phone: err.error.details.phone }));
						} else {
							errors.push(t('error.userDuplicate'));
						}
					}
				}
			}
			setFeedback({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
				errors: errors,
				response: cause.res,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSelectSheet = async (sheetName: string) => {
		if (!selectedFile || !isExcelFile(selectedFile.name)) return;

		// Clear previous data
		setCsvHeaders([]);
		setCsvRows([]);
		setFeedback(null);

		try {
			const result = await parseExcel({ file: selectedFile, sheetName });
			setSelectedSheet(sheetName);
			setCsvHeaders(result.headers);
			setCsvRows(result.rows);
		} catch (error) {
			setFeedback({
				alert: {
					children: t('error.sheetParsingFailed'),
					severity: 'error',
				},
				errors: [t('error.sheetNotFound', { sheetName })],
			});
		}
	};

	const handleCsv = (text: string) => {
		if (text) {
			try {
				const { rows, headers } = parseCsv({ string: text });

				// Validate that we have headers
				if (headers.length === 0) {
					throw new Error('CSV file appears to be empty or invalid', {
						cause: {
							id: 'CSV_EMPTY_OR_INVALID',
						},
					});
				}

				// set csv headers and rows
				setCsvHeaders(headers);
				setCsvRows(rows);

				// Warn if replacement characters were found (encoding issue)
				const warningsNew: string[] = [];
				if (text.includes('\uFFFD')) {
					warningsNew.push(t('warning.csvEncodingIssue'));
				}
				setWarnings(warningsNew);

				// Clear any previous errors
				setFeedback(null);
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				setFeedback({
					alert: {
						children: translateError({ cause, t }),
						severity: 'error',
					},
				});
				setWarnings([]);
			}
		}
	};

	// fetch form and loans when beneficiary type changes
	useEffect(() => {
		if (!isWfa || (isWfa && entity)) {
			fetchLoans();
			fetchForm();
		}
	}, [beneficiaryType, entity, fetchForm, fetchLoans, isWfa]);

	// Show sheet selector for Excel files with multiple sheets
	const hasSheetSelect = availableSheets.length > 1;
	const hasData = csvHeaders.length > 0 && csvRows.length > 0;

	let colSizeEntity = 6;
	let colSizeSheet = 6;
	let colSizeFinancingSources = 12;

	if (isWfa && hasSheetSelect) {
		colSizeEntity = 4;
		colSizeSheet = 4;
		colSizeFinancingSources = 4;
	} else if (isWfa || hasSheetSelect) {
		colSizeFinancingSources = 6;
	}

	return (
		<form id={formId} name="formUploadBeneficiaries" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<InputFile
						hasButton={true}
						helperText={t('acceptedFileFormats', { formats: '.csv, .xlsx, .xls' }) + '. '}
						id={`${formId}-file-picker`}
						inputProps={{ accept: '.csv,.xlsx,.xls' }}
						label={t('selectFile')}
						onChange={handleSelectFile}
						required={true}
					/>
				</Grid>
				{hasSheetSelect && (
					<Grid item xs={12} lg={colSizeSheet}>
						<Select
							id={`${formId}-sheet`}
							label={t('selectSheet')}
							onChange={(e) => {
								const { value } = e.target as HTMLInputElement;
								handleSelectSheet(value);
							}}
							options={optionsSheets}
							required={true}
							value={selectedSheet}
						/>
					</Grid>
				)}
				{isWfa && (
					<Grid item xs={12} lg={colSizeEntity}>
						<Select
							id={`${formId}-entity`}
							label={t('entity')}
							name="entity"
							onChange={(e) => {
								const { value } = e.target as HTMLInputElement;
								setEntity(value);
							}}
							options={optionsEntities}
							required={true}
							value={entity}
						/>
					</Grid>
				)}
				<Grid item xs={12} lg={colSizeFinancingSources}>
					<Select
						disabled={isWfa && !entity}
						hasAllOption={true}
						id={`${formId}-loans`}
						label={t('financingSources')}
						multiple={true}
						name="loans"
						options={optionsLoans}
						required={true}
					/>
				</Grid>
				<Grid item xs={12}>
					<RadioButtons
						id={`${formId}-type`}
						defaultValue={beneficiaryType}
						inputs={radioInputs}
						label={t('type')}
						name="type"
						onChange={handleChangeBeneficiaryType}
						required={true}
					/>
				</Grid>
				{hasData && (
					<Grid item xs={12}>
						<FormTableFields
							fields={fields}
							formId={formId}
							options={optionsHeaders}
							rows={csvRows}
							setFields={setFields}
							titleTotal={t('totalBeneficiaries')}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								defaultChecked={true}
								defaultValue="on"
								id={`${formId}-is-kyc-verified`}
								name="isKycVerified"
								required={true}
							/>
						}
						label={t('formUploadBeneficiaries.kycAml')}
					/>
				</Grid>
				{warnings.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<AlertTitle>{t('warnings')}</AlertTitle>
							<Box component="ul">
								{warnings.map((warning, index) => (
									<Box component="li" key={index}>
										{warning}
									</Box>
								))}
							</Box>
						</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
